import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import {useDeleteProductionSet} from '../../../hooks/productions/useProductions';
import {IDeleteSetPayload} from '../../../api/types/production.type';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';

const DeleteProductionSetApiTest: React.FC = () => {
  const [securityGrpId, setSecurityGrpId] = useState<number>(1);
  const [productionSetId, setProductionSetId] = useState<number>(14);
  const [selectedView, setSelectedView] = useState<string>('Tile');
  const [projectId, setProjectId] = useState<number>(957);
  const [isProductionSet, setIsProductionSet] = useState<boolean>(true);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const deleteSetMutation = useDeleteProductionSet(
    (data) => {
      console.log('Production set deleted:', data);
    },
    (error) => {
      console.error('Error deleting production set:', error);
    }
  );

  const buildPayload = (): IDeleteSetPayload => ({
    SecurityGrpId: securityGrpId,
    ProductionSetId: productionSetId,
    SelectedView: selectedView,
    ProjectId: projectId,
    IsProductionSet: isProductionSet,
  });

  const handleTestApi = () => {
    deleteSetMutation.mutate(buildPayload());
  };

  const handleReset = () => {
    setSecurityGrpId(1);
    setProductionSetId(14);
    setSelectedView('Tile');
    setProjectId(957);
    setIsProductionSet(true);
  };

  const copyToClipboard = async (data: unknown) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyPayload = () => copyToClipboard(buildPayload());

  const handleCopyResponse = () => {
    if (deleteSetMutation.data) {
      copyToClipboard(deleteSetMutation.data);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Delete Production Set API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Delete an existing production/export set by supplying the security
        group, project, and set identifiers.
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Payload
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGrpId}
            onChange={(e) => setSecurityGrpId(Number(e.target.value))}
            size="small"
            placeholder="Enter security group ID"
          />
          <TextField
            label="Production Set ID"
            type="number"
            value={productionSetId}
            onChange={(e) => setProductionSetId(Number(e.target.value))}
            size="small"
            placeholder="Enter production set ID"
          />
          <TextField
            label="Selected View"
            type="text"
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
            size="small"
            placeholder="Enter selected view"
          />
          <TextField
            label="Project ID"
            type="number"
            value={projectId}
            onChange={(e) => setProjectId(Number(e.target.value))}
            size="small"
            placeholder="Enter project ID"
          />
          <TextField
            label="Is Production Set"
            type="text"
            value={isProductionSet ? 'true' : 'false'}
            onChange={(e) => setIsProductionSet(e.target.value === 'true')}
            size="small"
            placeholder="true/false"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          <Button
            variant="contained"
            color="error"
            onClick={handleTestApi}
            disabled={deleteSetMutation.isPending}
            data-testid="delete-production-set-api-test-button">
            {deleteSetMutation.isPending ? '⏳ Deleting...' : '🗑️ Delete Set'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="delete-production-set-api-test-reset-button">
            🔄 Reset
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopyPayload}
            data-testid="delete-production-set-api-test-copy-payload-button">
            📋 Copy Payload
          </Button>
          <Button
            variant="outlined"
            onClick={handleCopyResponse}
            disabled={!deleteSetMutation.data}
            data-testid="delete-production-set-api-test-copy-response-button">
            📋 Copy Response
          </Button>
        </Box>

        {copySuccess && (
          <Typography sx={{marginTop: 1}} className="typo-text caption1_size">
            ✅ Copied to clipboard!
          </Typography>
        )}
      </Box>
      {deleteSetMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Deleting production set... Making API request...
          </Typography>
        </Box>
      )}
      {deleteSetMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {deleteSetMutation.error.message}
          </Typography>
        </Box>
      )}
      {deleteSetMutation.data && !deleteSetMutation.isPending && (
        <Accordion
          defaultExpanded
          sx={{
            marginTop: 3,
            boxShadow: 2,
            borderRadius: 1,
            '&:before': {display: 'none'},
          }}
        >
          <AccordionSummary
            expandIcon={<CaretDown />}
            sx={{
              backgroundColor: '#f0f7ff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Typography className="typo-text subhead2_size">
              ✅ Response Payload
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: '#fafafa',
              borderRadius: '0 0 8px 8px',
            }}
          >
            <pre
              style={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '16px',
                borderRadius: '8px',
                overflowX: 'auto',
                margin: 0,
              }}
            >
              {JSON.stringify(deleteSetMutation.data, null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          '&:before': {display: 'none'},
        }}
      >
        <AccordionSummary
          expandIcon={<CaretDown />}
          sx={{
            backgroundColor: '#f0f7ff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography className="typo-text subhead2_size">
            📘 Sample Payload & Response
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: '#fafafa',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{color: '#555', marginBottom: 1}}
            className="typo-text subhead3_size"
          >
            Sample Request:
          </Typography>
          <pre
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              padding: '16px',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(buildPayload(), null, 2)}
          </pre>

          <Typography
            variant="subtitle1"
            sx={{color: '#555', marginTop: 2, marginBottom: 1}}
            className="typo-text subhead3_size"
          >
            Sample Response:
          </Typography>
          <pre
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              padding: '16px',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            {`{
  "message": "Production Set has been deleted successfully.",
  "success": false,
  "field": null
}`}
          </pre>
        </AccordionDetails>
      </Accordion>
      <Box sx={{marginTop: 3, display: 'flex', justifyContent: 'flex-end'}}>
        <ArrButton
          variant="contained"
          size="medium"
          label="Delete Production Set"
          onClick={handleTestApi}
          isLoading={deleteSetMutation.isPending}
          color="error"
          dataTestId="delete-production-set-api-test-delete-production-set-button" />
      </Box>
    </Box>
  );
};

export default DeleteProductionSetApiTest;
